import { useState } from "react";
import { getDocumentSignedUrl } from "@/lib/affiliateUpload";
import { DetailSection, EmptyLine } from "./AfiliadoDetailBits";
import type { AfiliadoDocumento, AfiliadoVehiculo } from "@/types";

interface AfiliadoDocumentosProps {
  documentos: AfiliadoDocumento[];
  vehiculos: AfiliadoVehiculo[];
}

export default function AfiliadoDocumentos({ documentos, vehiculos }: AfiliadoDocumentosProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const handleOpen = async (path: string | null, id: string) => {
    if (!path) return;
    setError("");
    setLoadingId(id);
    try {
      const url = await getDocumentSignedUrl(path);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("No pudimos abrir el documento. Intenta nuevamente.");
    } finally {
      setLoadingId(null);
    }
  };

  const vehicleLabel = (vehicleId: string | null) => {
    if (!vehicleId) return "General";
    const index = vehiculos.findIndex((veh) => veh.id === vehicleId);
    if (index < 0) return "Vehículo";
    const veh = vehiculos[index];
    const nombre = [veh.marca, veh.modelo].filter(Boolean).join(" ") || veh.placa || "Vehículo";
    return `Vehículo ${index + 1} · ${nombre}`;
  };

  return (
    <DetailSection title={`Documentación privada (${documentos.length})`} icon="ri-folder-shield-line">
      <p className="mb-3 flex items-center gap-2 rounded-md border border-accent-300 bg-accent-50 px-3.5 py-2.5 text-xs text-accent-900">
        <i className="ri-lock-line text-sm leading-none" aria-hidden="true"></i>
        Los documentos se abren con un enlace temporal privado. Nunca son públicos.
      </p>
      {documentos.length === 0 ? (
        <EmptyLine>No se recibieron documentos.</EmptyLine>
      ) : (
        <ul className="space-y-2.5">
          {documentos.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 px-3.5 py-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-file-text-line text-base leading-none" aria-hidden="true"></i>
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-label font-medium text-foreground-900">
                    {doc.tipo ?? "Documento"}
                  </p>
                  <p className="truncate text-xs text-foreground-500">
                    {vehicleLabel(doc.vehicle_id)} · {doc.archivo_nombre ?? "archivo"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleOpen(doc.archivo_url, doc.id)}
                disabled={loadingId === doc.id}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-50"
              >
                <i
                  className={`${loadingId === doc.id ? "ri-loader-4-line animate-spin" : "ri-eye-line"} text-base leading-none`}
                  aria-hidden="true"
                ></i>
                Ver
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="mt-3 flex items-center gap-1 text-xs text-primary-700">
          <i className="ri-error-warning-line text-sm leading-none" aria-hidden="true"></i>
          {error}
        </p>
      )}
    </DetailSection>
  );
}